import {Component, ElementRef, OnDestroy, Self, ViewChild} from '@angular/core';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {PageInitAbstract} from '@wba/commons/abstracts/page-init.abstract';
import {Antrag} from '@wba/models/antrag';
import {ProgressNavItemsEnum} from '@wba/models/enums/progress-nav-items.enum';

import {AntragService} from '@wba/services/api/antrag/antrag.service';
import {AntragsdatenService} from '@wba/services/api/antragsdaten/antragsdaten.service';
import {MitgliedService} from '@wba/services/api/mitglied/mitglied.service';
import {ProgressNavigationService} from '@wba/services/commons/progress-navigation.service';
import {AntragLoadAction} from '@wba/store/actions/antrag.actions';
import {AppState} from '@wba/store/models/app-state';
import {FormUtils} from '@wba/utils/form.utils';
import {noop, Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {UrlParamService} from '@wba/services/commons/url-param.service';
import {BaCollapse} from '@design/bapf-pattern-library/source/js/_collapse';
import {getRouteInfo, mapNavItem, RouteInfo} from '@wba/containers/antragsstrecke/zusammenfassung/route-map';
import {ProgressStatusEnum} from '@wba/models/enums/progress-status.enum';


@Component({
  selector: 'wba-zusammenfassung',
  templateUrl: './zusammenfassung.component.html',
  styleUrls: ['./zusammenfassung.component.scss'],
  providers: [UrlParamService],
  standalone: false
})
export class ZusammenfassungComponent extends PageInitAbstract implements OnDestroy {
  // es wäre schön, wenn dieses Array nicht so verwendet würde, besser die IDs direkt im HTML verwenden. Ist doch nur verwirrend!
  public readonly ZMF_PASSEDSENT_TEXT_IDS =
    [
      'ZMF_PassedSent_Text',
      'ZMF_PassedSent_Text1',
      'ZMF_PassedSent_Text2',
      'ZMF_PassedSent_Text3',
      'ZMF_PassedSent_Text4',
      'ZMF_PassedSent_Text5',
      'ZMF_PassedSent_Text6',
      'ZMF_PassedSent_Text7',
      'ZMF_PassedSent_Text8',
      'ZMF_PassedSent_Text9',
      'ZMF_PassedSent_Text10'
    ];

  readonly FFA_00012: string = 'FFA_00012';
  readonly TFA_00031: string = 'TFA_00031';

  @ViewChild('button') buttonElement: ElementRef;

  antrag$: Observable<Antrag>;
  drafts: string[];
  mitglieder$: Observable<{ id: string, name: string }[]>;
  form: UntypedFormGroup;
  pending: boolean = false;
  showFehler: boolean = false;
  isFachlicherFehler: boolean = false;
  fachlicheFehler: string = '';
  instanceOfCollapse!: BaCollapse;
  private readonly unsubscriber$ = new Subject<void>();
  private navigate = noop;
  routeInfos: RouteInfo[] = [];
  wbaAntragVersion: string;
  antragId: string;
  titleTechnischerFehler: string = '';
  isAllgemeinerTechnischerFehler: boolean = true;

  constructor(private readonly route: ActivatedRoute,
              private readonly router: Router,
              private readonly antragsdatenService: AntragsdatenService,
              private readonly store: Store<AppState>,
              private readonly antragService: AntragService,
              private readonly mitgliedService: MitgliedService,
              private readonly progressNavigationService: ProgressNavigationService,
              @Self() private readonly urlParamService: UrlParamService,
  ) {
    super();

    this.wbaAntragVersion = urlParamService.getWbaFormVersion();
    this.antragId = urlParamService.getWbaAntragId();

    this.form = new UntypedFormGroup({
      checkbox1: new UntypedFormControl(false, checkboxCheckedValidator('Bestätigung der gemachten Angaben ist ein Pflichtfeld.')),
    });

    this.antrag$ = this.store.select(state => state.antrag);
    this.mitglieder$ = this.mitgliedService.getMitgliederFuerAnlageEk(this.route.snapshot.queryParams.id);

    this.store.dispatch(new AntragLoadAction(this.route.snapshot.queryParams.id));
    this.antragsdatenService.getAllDraftSchluessel(this.route.snapshot.queryParams.id).subscribe(value => {
      this.drafts = value;
    });
  }

  senden() {
    this.antragsdatenService.getAllDraftSchluessel(this.route.snapshot.queryParams.id).subscribe(value => {
      this.drafts = value;
    });

    this.navigate = () => {
      this.router.navigate(['./abschluss'], {queryParams: {id: this.route.snapshot.queryParams.id, v: this.route.snapshot.queryParams.v}});
    };

    if (this.form.valid) {
      this.pending = true;
      this.antragService.finalizeAntrag(this.route.snapshot.queryParams.id).pipe(
        takeUntil(this.unsubscriber$)
      ).subscribe({
        next: () => this.navigate(),
        error: this.errorHandler,
        complete: () => this.pending = false
      });
    } else {
      FormUtils.markFormGroupTouched(this.form);
    }
  }

  errorHandler = (error) => {
    this.showFehler = true;
    if (Object.keys(error.errors).some(key => key.startsWith('FFA_'))) {
      this.isFachlicherFehler = true;
      if (Object.keys(error.errors).includes(this.FFA_00012)) {
        const schluessel = error.errors[this.FFA_00012].split(',');
        this.routeInfos = schluessel.map(item => getRouteInfo(item));
        mapNavItem(schluessel).forEach(val => this.progressNavigationService.setStatus(val, ProgressStatusEnum.ERROR));
        delete error.errors[this.FFA_00012];
      }
      this.fachlicheFehler = Object.values(error.errors).join('\n');
    } else if (Object.keys(error.errors).includes('TFA_00031')) {
      this.titleTechnischerFehler = 'Senden des Antrags nicht möglich';
      this.isAllgemeinerTechnischerFehler = false;
    } else {
      this.titleTechnischerFehler = 'Technischer Fehler';
      this.isAllgemeinerTechnischerFehler = true;
    }
    setTimeout(() => {
      const element = document.getElementById('Fehler_Box');
      if (element) {
        element.scrollIntoView({inline: 'center', block: 'center', behavior: 'smooth'});
        element.focus({preventScroll: true});
      }
    });
    this.pending = false;
  };

  zurueck() {
    this.progressNavigationService.prevPage(ProgressNavItemsEnum.ZUSAMMENFASSUNG);
  }

  ngOnDestroy() {
    this.unsubscriber$.next(null);
    this.unsubscriber$.complete();
  }

  ngAfterViewInit(): void {
    this.instanceOfCollapse = new BaCollapse(this.buttonElement.nativeElement);
  }

}

export function checkboxCheckedValidator(label: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: string } => {
    return !!control.value ? null : {typ: 'error', message: label};
  };
}
